import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { orderBy } from 'lodash';
import moment from 'moment';
import { History as HistoryIcon } from '@mui/icons-material';
import { Virtuoso } from 'react-virtuoso';
import { Box, Avatar } from '@mui/material';
import { getRescueClaimer } from '../../../../helpers/RescuesHelper';
import { Colors } from '../../../../assets/theme/Colors';
import { StyledSubHeader } from '../../../rescuerDashboard/rescuerDashboardMobile/Rescues/myFrus/MyFrusMobile';
import RescueMobileCard from '../../../Rescue/RescueMobileCard';
import { NoRescueCard } from '../../../rescuerDashboard/rescuerDashboardMobile/Rescues/RescuerRescuesDashboardMobileContainer';
import useActiveSite from '../../../../hooks/useActiveSite';
import { fetchSitePastRescuesIfNeeded } from '../../../../actions/sites';

const ReceiverMobileRecentRescues = ({ receiverId }) => {
  const site = useActiveSite();
  const dispatch = useDispatch();
  const sitesPastRescues = useSelector((state) => state.entities.sites.pastRescues);
  const rescuesList = Object.values(sitesPastRescues.byId);
  const filteredRescues = rescuesList.filter((rescue) => rescue.receiver_id === receiverId);

  useEffect(() => {
    if (site?.id) {
      dispatch(
        fetchSitePastRescuesIfNeeded(
          site.id,
          moment().subtract(31, 'days').format('YYYYMMDD'),
          moment().subtract(1, 'days').format('YYYYMMDD')
        )
      );
    }
  }, [site]);

  const recentRescuesSorted = orderBy(
    filteredRescues,
    (filteredRescue) => moment(`${filteredRescue.date} ${filteredRescue.pickup_begin}`, 'YYYYMMDD HH:mm:ss'),
    ['desc']
  );

  const recentRescuesCount = recentRescuesSorted.length;

  const isInflight = sitesPastRescues.inflight;

  if (isInflight) {
    return <RescueMobileCard isLoading hideExpandPanel hideDonorLink isReceiverView />;
  }

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'auto',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 1, 0),
        display: 'flex',
        flexDirection: 'column',
      })}
    >
      {!isInflight && filteredRescues.length === 0 && <NoRescueCard text="No rescues received in the past 30 days." />}

      {filteredRescues.length > 0 && (
        <Box display="flex" flex={1} flexDirection="column" data-introid="schedule-rescues-container">
          <Box px={1}>
            <StyledSubHeader
              color={Colors.rescues.recent.color}
              text={`${moment().subtract(31, 'days').format('MMMM Do')} - ${moment()
                .subtract(1, 'days')
                .format('MMMM Do')}`}
              subText="Showing rescues from the last 30 days"
              icon={
                <Avatar
                  sx={[
                    { borderRadius: '12', width: 50, height: 50 },
                    { backgroundColor: Colors.rescues.recent.color, filter: 'brightness(0.9)' },
                  ]}
                >
                  <HistoryIcon />
                </Avatar>
              }
            />
          </Box>
          <Virtuoso
            style={{ width: '100%', height: '100%' }}
            totalCount={recentRescuesCount}
            itemContent={(index) => {
              const rescue = recentRescuesSorted[index];
              const claimer = getRescueClaimer(rescue);
              return (
                <Box pt={1} pb={1}>
                  <RescueMobileCard
                    isLoading={isInflight}
                    key={rescue.id}
                    rescue={rescue}
                    claimer={claimer}
                    isCancelled={rescue.cancelled_by_id !== null}
                    hideExpandPanel
                    hideDonorLink
                    isReceiverView
                    isRecent
                    my={2}
                  />
                </Box>
              );
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ReceiverMobileRecentRescues;
